import { useCallback } from 'react';

import { perfMarkStart } from '@confluence/performance/entry-points/performance.ts';
import PageTreePreviousItemsQuery from './queries/__generated__/PageTreePreviousItemsQuery.graphql.ts';
import PageTreeFollowingItemsQuery from './queries/__generated__/PageTreeFollowingItemsQuery.graphql.ts';
import { usePageTreeState } from './usePageTreeState.ts';
import { useImperativeQuery } from './useImperativeQuery.ts';
import {
	isPageTreeTruncatedPrevious,
	isPageTreeTruncatedFollowing,
	mergePreviousPages,
	mergeFollowingPages,
	getDirectionalCursors,
} from './data-extractors.ts';
import type { GraphQLPageStatus } from './pageTreeStatuses.ts';
import { INITIAL_PAGE_SIZE } from './paginationLimits.ts';

type direction = 'previous' | 'following';

type useLoadMorePages = ({
	statuses,
}: {
	statuses?: GraphQLPageStatus[];
	direction?: direction;
}) => {
	loadAllPages: (
		contentId: string,
		spaceKey: string,
		isSuperAdmin: boolean,
		cursors: any,
		allPages: any,
	) => any;
};

const isPageTreeTruncatedDirectionally = {
	previous: isPageTreeTruncatedPrevious,
	following: isPageTreeTruncatedFollowing,
};

const mergePages = {
	previous: mergePreviousPages,
	following: mergeFollowingPages,
};

export const useLoadMorePages = ({
	statuses,
}: {
	statuses: any;
}): {
	loadMorePages: (
		contentId: any,
		spaceKey: any,
		isSuperAdmin: any,
		cursors: any,
		allPages: any,
		direction?: direction,
		previousCounts?: {
			children: number;
			previousSiblings: number;
			followingSiblings: number;
		},
	) => any;
} => {
	// eslint-disable-next-line confluence-sweet-state/sweet-state-prefer-actions-hook
	const [rootId, actions] = usePageTreeState();

	const getMorePreviousPages = useImperativeQuery(PageTreePreviousItemsQuery);
	const getMoreFollowingPages = useImperativeQuery(PageTreeFollowingItemsQuery);

	const loadMorePages = useCallback(
		(
			contentId: any,
			spaceKey: any,
			isSuperAdmin: any,
			cursors: any,
			allPages: any,
			direction: direction = 'following',
			previousCounts: {
				children: number;
				previousSiblings: number;
				followingSiblings: number;
			} = {
				children: 0,
				previousSiblings: 0,
				followingSiblings: 0,
			},
		): any => {
			perfMarkStart({ subject: 'PageTree', subjectId: 'loadMorePages' });

			const id = cursors?.[direction]?.id || undefined;
			const ancestorIndex = cursors?.[direction]?.ancestorIndex || undefined;
			const directionalGetMorePages = {
				previous: getMorePreviousPages,
				following: getMoreFollowingPages,
			}[direction];

			return directionalGetMorePages({
				contentId: id,
				paginationLimit: INITIAL_PAGE_SIZE,
				spaceKey,
				isSuperAdmin,
				statuses,
				ptStatuses: statuses,
			})
				.then((response) => {
					const { data, error } = response as any;
					if (error) {
						actions.handleLoadingError(error, direction);
						return;
					} else {
						if (data && Object.keys(data).length > 0) {
							const { currentPage, rootLevelPages } = data;
							const mergedPages = mergePages[direction](currentPage, allPages, ancestorIndex);
							const childrenLength = mergedPages.children.count;
							const previousSiblingsLength = mergedPages.previousSiblings.count;
							const followingSiblingsLength = mergedPages.followingSiblings.count;
							//infinite loop check:
							if (
								childrenLength > previousCounts.children ||
								previousSiblingsLength > previousCounts.previousSiblings ||
								followingSiblingsLength > previousCounts.followingSiblings
							) {
								//congrats one of the counts got bigger, paging was worth it
								if (isPageTreeTruncatedDirectionally[direction](currentPage)) {
									const nextCursors = getDirectionalCursors(
										currentPage,
										rootId,
										cursors,
										direction,
									);
									return loadMorePages(
										contentId,
										spaceKey,
										isSuperAdmin,
										nextCursors,
										mergedPages,
										direction,
										{
											children: childrenLength,
											previousSiblings: previousSiblingsLength,
											followingSiblings: followingSiblingsLength,
										},
									);
								}
							}
							return { mergedPages, rootLevelPages };
						}
					}
				})
				.catch((error) => {
					actions.handleLoadingError(error, direction);
					return { error };
				});
		},
		[getMorePreviousPages, getMoreFollowingPages, actions, statuses, rootId],
	);

	return { loadMorePages };
};
